import { useEffect, useState } from 'react';

interface UseResetPasswordModalViewModelProps {
    isOpen: boolean;
    initialEmail: string;
    onResetPassword: (email: string) => Promise<void>;
}

/** Shape check only; the authoritative validation is Firebase rejecting the address. */
const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const useResetPasswordModalViewModel = ({
    isOpen,
    initialEmail,
    onResetPassword
}: UseResetPasswordModalViewModelProps) => {
    const [email, setEmail] = useState(initialEmail);
    const [errorMessage, setErrorMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [wasSent, setWasSent] = useState(false);

    // Reopening the modal starts a fresh attempt, prefilled with whatever the user
    // had already typed into the login form.
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setEmail(initialEmail);
        setErrorMessage('');
        setIsSending(false);
        setWasSent(false);
    }, [isOpen, initialEmail]);

    const updateEmail = (value: string) => {
        setEmail(value);
        setErrorMessage('');
    };

    const sendResetEmail = async () => {
        const trimmedEmail = email.trim();

        if (!trimmedEmail) {
            setErrorMessage("Por favor, ingresá tu email.".localized());
            return;
        }

        if (!isValidEmail(trimmedEmail)) {
            setErrorMessage("Por favor, ingresá un Email válido.".localized());
            return;
        }

        setIsSending(true);
        setErrorMessage('');

        try {
            await onResetPassword(trimmedEmail);
            setWasSent(true);
        } catch (error: any) {
            switch (error?.code) {
                case 'auth/invalid-email':
                    setErrorMessage("Por favor, ingresá un Email válido.".localized());
                    break;
                case 'auth/too-many-requests':
                    setErrorMessage("Hiciste demasiados intentos. Esperá unos minutos y probá de nuevo.".localized());
                    break;
                case 'auth/network-request-failed':
                    setErrorMessage("No pudimos conectarnos. Revisá tu conexión e intentá de nuevo.".localized());
                    break;
                default:
                    setErrorMessage("Algo salió mal. Por favor, intentá de nuevo.".localized());
                    break;
            }
            console.error(error);
        } finally {
            setIsSending(false);
        }
    };

    return {
        email,
        updateEmail,
        errorMessage,
        isSending,
        wasSent,
        sendResetEmail
    };
};
